/**
 * `MiniChart` — the 220 × 74 sparkline on every fleet machine card.
 *
 * A verbatim port of `miniChart(vals, dates, unit, col, limit)` at line 2134 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`. Every dimension,
 * padding, tick count, font size, dash pattern, fill opacity and rounding rule
 * is the demo's; nothing is recomputed or "improved".
 *
 * ## Deviations from the source
 *
 * 1. **Colours are tokens.** The demo writes `var(--line)`, `var(--line-2)`,
 *    `var(--muted)` and `var(--vpoor)`. This repo's token names differ, so they
 *    map onto `--hairline-strong`, `--hairline`, `--ink-tertiary` and
 *    `--band-vpoor` (all confirmed in `styles/tokens.css` / `styles/oem.css`).
 *    The series colour still arrives as a prop, because the caller picks it
 *    from the machine's condition band.
 * 2. **Fixed `viewBox`.** The demo's too — `0 0 220 74`, never data-derived, so
 *    two renders of the same series are byte-identical and snapshots hold.
 * 3. **Fonts inline.** The demo names `IBM Plex Mono` / `IBM Plex Sans` on the
 *    SVG text nodes. Tailwind maps `font-mono` to Plex Mono in this repo, but
 *    SVG `<text>` does not inherit a Tailwind class through
 *    `dangerouslySetInnerHTML`-free React the way the demo's markup did, so the
 *    family is set per node exactly as the demo set it.
 *
 * No `Date.now()`, no `Math.random()`, no counters — the component is a pure
 * function of its props.
 *
 * Shared viz (UI layer support): no engine calls, no data fetching.
 *
 * @see src/versions/v1/modules/fleet/FleetHealthScene.tsx — its one caller today
 * @see docs/foundations/4-layer-architecture.md
 */

/** Demo line 2134. `miniChart`'s fixed geometry. */
const W = 220;
const H = 74;
const PAD_LEFT = 30;
const PAD_RIGHT = 6;
const PAD_TOP = 6;
const PAD_BOTTOM = 16;

/** `var(--line)` in the demo. */
const AXIS = 'var(--hairline-strong)';
/** `var(--line-2)` in the demo — the dashed gridlines. */
const GRID = 'var(--hairline)';
/** `var(--muted)` in the demo — axis numerals and the date rail. */
const MUTED = 'var(--ink-tertiary)';
/** `var(--vpoor)` in the demo — the alarm/limit rule. */
const LIMIT = 'var(--band-vpoor)';

const MONO = 'IBM Plex Mono,monospace';
const SANS = 'IBM Plex Sans,sans-serif';

/** Demo line 2134. `fmtD` — `2026-09-04` → `04/09`. */
const fmtD = (date: string): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

export interface MiniChartProps {
  /** The daily series, in source order. 12 points in the seed data. */
  values: readonly number[];
  /** Dates parallel to {@link MiniChartProps.values}; only first and last print. */
  dates: readonly string[];
  /** Unit suffix for the accessible label and the limit caption, e.g. `'°C'`. */
  unit: string;
  /** Series colour — a CSS value, normally `var(--band-…)` for the machine. */
  colour: string;
  /** Alarm line, or `null` when the metric has none. */
  limit?: number | null;
}

/**
 * Demo line 2134. `miniChart` — a 12-day daily mean with an optional alarm
 * line, three y-ticks and a first/last date rail.
 *
 * Renders nothing when there is no series: the demo's `x()` divides by
 * `vals.length - 1`, which is `Infinity` for one point and `NaN` for none, so
 * a machine with fewer than two readings got a broken chart. Guarding is the
 * one behavioural fix in this file.
 */
export function MiniChart({ values, dates, unit, colour, limit = null }: MiniChartProps) {
  if (values.length < 2) return null;

  const all = limit != null ? [...values, limit] : [...values];
  const pad = (Math.max(...all) - Math.min(...all)) * 0.12 || 1;
  const mn = Math.floor(Math.min(...all) - pad);
  const mx = Math.ceil(Math.max(...all) + pad);

  const x = (i: number) => PAD_LEFT + (i / (values.length - 1)) * (W - PAD_LEFT - PAD_RIGHT);
  const y = (v: number) => PAD_TOP + (1 - (v - mn) / (mx - mn)) * (H - PAD_TOP - PAD_BOTTOM);

  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const ticks = [mn, Math.round((mn + mx) / 2), mx];
  const last = values.length - 1;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${unit} over the last ${values.length} days`}
      style={{ height: H, width: '100%' }}
    >
      <line x1={PAD_LEFT} y1={PAD_TOP} x2={PAD_LEFT} y2={H - PAD_BOTTOM} stroke={AXIS} />
      <line x1={PAD_LEFT} y1={H - PAD_BOTTOM} x2={W - PAD_RIGHT} y2={H - PAD_BOTTOM} stroke={AXIS} />

      {ticks.map((v, i) => (
        <g key={`tick-${i}-${v}`}>
          <text
            x={PAD_LEFT - 4}
            y={y(v) + 3}
            textAnchor="end"
            fontSize={8}
            fill={MUTED}
            fontFamily={MONO}
          >
            {v}
          </text>
          <line
            x1={PAD_LEFT}
            y1={y(v)}
            x2={W - PAD_RIGHT}
            y2={y(v)}
            stroke={GRID}
            strokeDasharray="2 3"
          />
        </g>
      ))}

      {limit != null ? (
        <g>
          <line
            x1={PAD_LEFT}
            y1={y(limit)}
            x2={W - PAD_RIGHT}
            y2={y(limit)}
            stroke={LIMIT}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          <text
            x={W - PAD_RIGHT}
            y={y(limit) - 2}
            textAnchor="end"
            fontSize={7.5}
            fill={LIMIT}
            fontFamily={SANS}
          >
            {`limit ${limit} ${unit}`}
          </text>
        </g>
      ) : null}

      <polyline
        points={`${PAD_LEFT},${H - PAD_BOTTOM} ${pts} ${x(last)},${H - PAD_BOTTOM}`}
        fill={colour}
        fillOpacity={0.08}
        stroke="none"
      />
      <polyline points={pts} fill="none" stroke={colour} strokeWidth={1.6} />
      <circle cx={x(last)} cy={y(values[last])} r={2.4} fill={colour} />

      <text x={PAD_LEFT} y={H - 4} fontSize={7.5} fill={MUTED} fontFamily={MONO}>
        {fmtD(dates[0] ?? '')}
      </text>
      <text
        x={(PAD_LEFT + W - PAD_RIGHT) / 2}
        y={H - 4}
        textAnchor="middle"
        fontSize={7.5}
        fill={MUTED}
        fontFamily={SANS}
      >
        date (dd/mm) · daily mean
      </text>
      <text
        x={W - PAD_RIGHT}
        y={H - 4}
        textAnchor="end"
        fontSize={7.5}
        fill={MUTED}
        fontFamily={MONO}
      >
        {fmtD(dates[dates.length - 1] ?? '')}
      </text>
    </svg>
  );
}
